// src/components/game/BugHunter.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useScore } from '../../context/ScoreContext';

const BugHunter = ({ isDarkMode }) => {
  const { updateScore, getScore } = useScore();
  const [bestScore, setBestScore] = useState(0);

  const bugs = [
    {
      code: 'const x = "5" + 2; // შედეგი?',
      options: ['7', '"52"', 'NaN', 'Error'],
      correct: 1,
      explanation: 'JavaScript-ში + ოპერატორი სტრინგთან აერთებს',
      difficulty: 'easy'
    },
    {
      code: 'function test() { return; } // რას აბრუნებს?',
      options: ['undefined', 'null', 'void', 'Error'],
      correct: 0,
      explanation: 'return; აბრუნებს undefined-ს',
      difficulty: 'easy'
    },
    {
      code: 'const arr = [1,2,3]; arr.length = 0; // arr?',
      options: ['[]', '[1,2,3]', '[null]', 'Error'],
      correct: 0,
      explanation: 'length=0 შლის ყველა ელემენტს',
      difficulty: 'medium'
    },
    {
      code: 'console.log(typeof NaN); // ?',
      options: ['number', 'NaN', 'undefined', 'object'],
      correct: 0,
      explanation: 'NaN არის number ტიპი',
      difficulty: 'easy'
    },
    {
      code: 'const obj = { a: 1 }; obj.b = obj.a + 1; // obj.b?',
      options: ['1', '2', 'undefined', 'NaN'],
      correct: 1,
      explanation: 'obj.b = 1 + 1 = 2',
      difficulty: 'easy'
    },
    {
      code: 'console.log([] + []); // ?',
      options: ['""', '[]', '0', 'Error'],
      correct: 0,
      explanation: '[] + [] => "" + "" => ""',
      difficulty: 'hard'
    },
    {
      code: 'typeof null === "object"; // ?',
      options: ['true', 'false', 'undefined', 'Error'],
      correct: 0,
      explanation: 'JavaScript-ის ცნობილი bug - typeof null === "object"',
      difficulty: 'medium'
    },
    {
      code: '0.1 + 0.2 === 0.3; // ?',
      options: ['true', 'false', 'undefined', 'Error'],
      correct: 1,
      explanation: 'ცურვადი წერტილების შეცდომა - 0.1 + 0.2 = 0.30000000000000004',
      difficulty: 'hard'
    }
  ];

  const [current, setCurrent] = useState(0);
  const [score, setScore] = useState(0);
  const [showResult, setShowResult] = useState(false);
  const [selected, setSelected] = useState(null);
  const [answered, setAnswered] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);
  const [time, setTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [shuffledBugs, setShuffledBugs] = useState([]);

  useEffect(() => {
    const saved = getScore('bug');
    setBestScore(saved || 0);
  }, [getScore]);

  useEffect(() => {
    const shuffled = [...bugs];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    setShuffledBugs(shuffled);
  }, []);

  useEffect(() => {
    let timer;
    if (isPlaying && !showResult) {
      timer = setInterval(() => setTime(t => t + 1), 1000);
    }
    return () => clearInterval(timer);
  }, [isPlaying, showResult]);

  const getDifficultyEmoji = (diff) => {
    const map = { easy: '🟢', medium: '🟡', hard: '🔴' };
    return map[diff] || '';
  };

  const getDifficultyText = (diff) => {
    const map = { easy: 'მარტივი', medium: 'საშუალო', hard: 'რთული' };
    return map[diff] || '';
  };

  const handleAnswer = (index) => {
    if (answered) return;
    setSelected(index);
    setAnswered(true);
    setShowExplanation(true);
    setIsPlaying(true);

    const isCorrect = index === shuffledBugs[current]?.correct;
    if (isCorrect) {
      setScore(prev => prev + 1);
    }

    setTimeout(() => {
      if (current < shuffledBugs.length - 1) {
        setCurrent(prev => prev + 1);
        setSelected(null);
        setAnswered(false);
        setShowExplanation(false);
      } else {
        setShowResult(true);
        setIsPlaying(false);
        const finalScore = score + (isCorrect ? 1 : 0);
        if (finalScore > bestScore) {
          updateScore('bug', finalScore);
          setBestScore(getScore('bug'));
        } else {
          updateScore('bug', finalScore);
          setBestScore(getScore('bug'));
        }
      }
    }, 3000);
  };

  const restart = () => {
    const shuffled = [...bugs];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    setShuffledBugs(shuffled);
    setCurrent(0);
    setScore(0);
    setShowResult(false);
    setSelected(null);
    setAnswered(false);
    setShowExplanation(false);
    setTime(0);
    setIsPlaying(false);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };

  const pageStyle = {
    minHeight: '100vh',
    background: isDarkMode 
      ? 'linear-gradient(135deg, #0a0a1a 0%, #1a1a2e 100%)' 
      : 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)',
    padding: 'clamp(12px, 3vw, 20px)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  };

  const containerStyle = {
    maxWidth: 'min(600px, 95vw)',
    width: '100%',
    background: isDarkMode 
      ? 'rgba(26, 26, 46, 0.9)' 
      : 'rgba(255, 255, 255, 0.9)',
    padding: 'clamp(20px, 4vw, 30px)',
    borderRadius: 'clamp(15px, 2vw, 20px)',
    backdropFilter: 'blur(20px)',
    boxShadow: isDarkMode 
      ? '0 20px 60px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.1)' 
      : '0 20px 60px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.5)',
    border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.05)',
    animation: 'slideUp 0.6s ease-out'
  };

  const codeStyle = {
    fontFamily: 'monospace',
    fontSize: 'clamp(15px, 2vw, 18px)',
    padding: 'clamp(12px, 2vw, 15px)',
    background: isDarkMode ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.02)',
    borderRadius: 'clamp(8px, 1.5vw, 12px)',
    margin: 'clamp(15px, 2vw, 20px) 0',
    color: isDarkMode ? '#4ade80' : '#1a1a2e',
    border: isDarkMode ? '1px solid rgba(74,222,128,0.1)' : '1px solid rgba(0,0,0,0.05)',
    overflow: 'auto',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    lineHeight: '1.6'
  };

  return (
    <div style={pageStyle}>
      <style>
        {`
          @keyframes slideUp {
            from { opacity: 0; transform: translateY(30px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .game-title {
            background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-size: 200% auto;
            animation: shimmer 3s linear infinite;
          }
          @keyframes shimmer {
            0% { background-position: -200% center; }
            100% { background-position: 200% center; }
          }
        `}
      </style>

      <div style={containerStyle}>
        <Link to="/games" style={{
          display: 'inline-block',
          padding: 'clamp(6px, 1vw, 8px) clamp(16px, 2vw, 20px)',
          marginBottom: 'clamp(15px, 2vw, 20px)',
          background: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
          color: isDarkMode ? '#fff' : '#000',
          textDecoration: 'none',
          borderRadius: 'clamp(8px, 1vw, 12px)',
          transition: 'all 0.3s',
          backdropFilter: 'blur(10px)',
          border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.05)',
          fontSize: 'clamp(13px, 1.5vw, 15px)'
        }}
        onMouseEnter={(e) => {
          e.target.style.transform = 'translateX(-5px)';
          e.target.style.background = isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)';
        }}
        onMouseLeave={(e) => {
          e.target.style.transform = 'translateX(0)';
          e.target.style.background = isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)';
        }}>
          ← უკან
        </Link>

        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: 'clamp(12px, 1.5vw, 15px)',
          flexWrap: 'wrap',
          gap: 'clamp(8px, 1vw, 10px)'
        }}>
          <h2 style={{ margin: 0, fontSize: 'clamp(20px, 3vw, 26px)' }}>
            <span className="game-title">🐛 Bug Hunter</span>
          </h2>
          <div style={{ display: 'flex', gap: 'clamp(10px, 1.5vw, 15px)', alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ color: '#4ade80', fontWeight: 'bold', fontSize: 'clamp(13px, 1.5vw, 15px)' }}>
              🏆 {bestScore}
            </span>
            <span style={{ color: isDarkMode ? '#aaa' : '#666', fontSize: 'clamp(13px, 1.5vw, 15px)' }}>
              🎯 {score}
            </span>
          </div>
        </div>

        {!showResult ? (
          <>
            {/* Stats */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(70px, 1fr))',
              gap: 'clamp(6px, 1vw, 8px)',
              marginBottom: 'clamp(15px, 2vw, 20px)',
              padding: 'clamp(10px, 1.5vw, 15px)',
              background: isDarkMode ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.03)',
              borderRadius: 'clamp(10px, 1.5vw, 12px)',
              textAlign: 'center'
            }}>
              <div>
                <div style={{ fontSize: 'clamp(16px, 2vw, 18px)' }}>📝</div>
                <div style={{ fontSize: 'clamp(13px, 1.5vw, 14px)', color: isDarkMode ? '#fff' : '#000' }}>
                  {current + 1}/{shuffledBugs.length}
                </div>
                <div style={{ fontSize: 'clamp(8px, 0.8vw, 9px)', color: isDarkMode ? '#666' : '#999' }}>კითხვა</div>
              </div>
              <div>
                <div style={{ fontSize: 'clamp(16px, 2vw, 18px)' }}>⏱️</div>
                <div style={{ fontSize: 'clamp(13px, 1.5vw, 14px)', color: isDarkMode ? '#fff' : '#000' }}>
                  {formatTime(time)}
                </div>
                <div style={{ fontSize: 'clamp(8px, 0.8vw, 9px)', color: isDarkMode ? '#666' : '#999' }}>დრო</div>
              </div>
              <div>
                <div style={{ fontSize: 'clamp(16px, 2vw, 18px)' }}>✅</div>
                <div style={{ fontSize: 'clamp(13px, 1.5vw, 14px)', color: isDarkMode ? '#fff' : '#000' }}>
                  {score}
                </div>
                <div style={{ fontSize: 'clamp(8px, 0.8vw, 9px)', color: isDarkMode ? '#666' : '#999' }}>სწორი</div>
              </div>
            </div>

            {/* Difficulty */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 'clamp(8px, 1vw, 10px)'
            }}>
              <p style={{ 
                color: isDarkMode ? '#aaa' : '#666', 
                margin: 0,
                fontSize: 'clamp(12px, 1.5vw, 14px)'
              }}>
                {getDifficultyEmoji(shuffledBugs[current]?.difficulty || 'easy')} {getDifficultyText(shuffledBugs[current]?.difficulty || 'easy')}
              </p>
              <span style={{
                fontSize: 'clamp(11px, 1.2vw, 12px)',
                color: isDarkMode ? '#666' : '#999',
                background: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                padding: '2px 10px',
                borderRadius: '20px'
              }}>
                {current + 1}/{shuffledBugs.length}
              </span>
            </div>

            {/* Code */}
            <div style={codeStyle}>
              <span style={{ 
                color: isDarkMode ? '#666' : '#999', 
                fontSize: 'clamp(10px, 1vw, 12px)', 
                display: 'block', 
                marginBottom: '5px' 
              }}>
                💻 JavaScript
              </span>
              {shuffledBugs[current]?.code}
            </div>

            {/* Options */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'clamp(8px, 1vw, 10px)' }}>
              {shuffledBugs[current]?.options.map((opt, idx) => {
                const isCorrect = idx === shuffledBugs[current].correct;
                const isSelected = selected === idx;
                let buttonStyle = {
                  padding: 'clamp(12px, 1.5vw, 16px)',
                  borderRadius: 'clamp(8px, 1vw, 12px)',
                  border: '2px solid',
                  background: isDarkMode ? 'rgba(26,26,46,0.8)' : 'rgba(255,255,255,0.8)',
                  color: isDarkMode ? '#fff' : '#000',
                  cursor: answered ? 'default' : 'pointer',
                  fontSize: 'clamp(14px, 1.5vw, 16px)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  fontWeight: isSelected ? 'bold' : 'normal',
                  touchAction: 'manipulation',
                  WebkitTapHighlightColor: 'transparent'
                };

                if (isSelected) {
                  if (isCorrect) {
                    buttonStyle.borderColor = '#4ade80';
                    buttonStyle.background = 'linear-gradient(135deg, #4ade80, #22d3ee)';
                    buttonStyle.color = '#000';
                    buttonStyle.boxShadow = '0 4px 20px rgba(74,222,128,0.3)';
                  } else {
                    buttonStyle.borderColor = '#f87171';
                    buttonStyle.background = 'linear-gradient(135deg, #f87171, #ef4444)';
                    buttonStyle.color = '#fff';
                    buttonStyle.boxShadow = '0 4px 20px rgba(248,113,113,0.3)';
                  }
                } else if (answered && isCorrect) {
                  buttonStyle.borderColor = '#4ade80';
                  buttonStyle.background = 'rgba(74,222,128,0.1)';
                  buttonStyle.boxShadow = '0 0 30px rgba(74,222,128,0.1)';
                }

                return (
                  <button
                    key={idx}
                    onClick={() => handleAnswer(idx)}
                    style={buttonStyle}
                    disabled={answered}
                    onMouseEnter={(e) => {
                      if (!answered && !isSelected) {
                        e.target.style.transform = 'scale(1.02)';
                        e.target.style.borderColor = '#667eea';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!answered && !isSelected) {
                        e.target.style.transform = 'scale(1)';
                        e.target.style.borderColor = isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)';
                      }
                    }}
                  >
                    <span style={{ 
                      display: 'inline-block',
                      minWidth: '24px',
                      color: isSelected ? 'inherit' : (isDarkMode ? '#666' : '#999'),
                      fontWeight: 'bold'
                    }}>
                      {String.fromCharCode(65 + idx)}.
                    </span>
                    {opt}
                    {isSelected && (
                      <span style={{ marginLeft: '10px' }}>
                        {isCorrect ? ' ✅' : ' ❌'}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Explanation */}
            {showExplanation && (
              <div style={{
                marginTop: 'clamp(15px, 2vw, 20px)',
                padding: 'clamp(12px, 1.5vw, 16px)',
                background: isDarkMode ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.02)',
                borderRadius: 'clamp(8px, 1vw, 12px)',
                color: isDarkMode ? '#fff' : '#000',
                fontSize: 'clamp(13px, 1.5vw, 15px)',
                border: isDarkMode ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.05)',
                borderLeft: `4px solid ${selected === shuffledBugs[current]?.correct ? '#4ade80' : '#f87171'}`,
                animation: 'slideUp 0.3s ease-out'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>💡</span>
                  <span style={{ fontWeight: 'bold' }}>
                    {selected === shuffledBugs[current]?.correct ? '✅ სწორია! ' : '❌ არასწორია! '}
                  </span>
                </div>
                <p style={{ margin: '8px 0 0 0', color: isDarkMode ? '#aaa' : '#666' }}>
                  {shuffledBugs[current]?.explanation}
                </p>
              </div>
            )}
          </>
        ) : (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 'clamp(48px, 10vw, 64px)', margin: '20px 0' }}>
              {score >= shuffledBugs.length * 0.8 ? '🐛🔥' : 
               score >= shuffledBugs.length * 0.5 ? '🐛😊' : '🐛😅'}
            </div>
            <h2 style={{ color: isDarkMode ? '#fff' : '#000', fontSize: 'clamp(24px, 3vw, 32px)' }}>
              🏁 დასრულდა!
            </h2>
            <p style={{ 
              fontSize: 'clamp(28px, 5vw, 40px)', 
              color: isDarkMode ? '#fff' : '#000', 
              fontWeight: 'bold' 
            }}>
              {score}/{shuffledBugs.length}
            </p>
            <p style={{ 
              color: isDarkMode ? '#aaa' : '#666',
              fontSize: 'clamp(14px, 1.5vw, 18px)'
            }}>
              📊 სიზუსტე: {Math.round((score / shuffledBugs.length) * 100)}%
            </p>
            <p style={{ 
              color: isDarkMode ? '#aaa' : '#666',
              fontSize: 'clamp(12px, 1.5vw, 14px)'
            }}>
              ⏱️ დრო: {formatTime(time)}
            </p>
            <div style={{
              margin: '15px 0',
              padding: '12px',
              background: isDarkMode ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.03)',
              borderRadius: '10px'
            }}>
              <span style={{ color: '#FFD93D' }}>🏆</span>
              <span style={{ color: isDarkMode ? '#fff' : '#000', marginLeft: '8px' }}>
                საუკეთესო: {bestScore}
              </span>
            </div>
            <button onClick={restart} style={{
              padding: 'clamp(12px, 1.5vw, 16px) clamp(30px, 5vw, 50px)',
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              color: 'white',
              border: 'none',
              borderRadius: 'clamp(10px, 1.5vw, 12px)',
              fontSize: 'clamp(14px, 1.5vw, 18px)',
              cursor: 'pointer',
              marginTop: '20px',
              transition: 'all 0.3s',
              fontWeight: 'bold',
              boxShadow: '0 4px 20px rgba(245,87,108,0.3)',
              touchAction: 'manipulation',
              width: '100%',
              maxWidth: '300px'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px) scale(1.02)';
              e.target.style.boxShadow = '0 8px 30px rgba(245,87,108,0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0) scale(1)';
              e.target.style.boxShadow = '0 4px 20px rgba(245,87,108,0.3)';
            }}>
              🔄 თავიდან
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BugHunter;